import { isNullish } from '@/utils/helper'
import { OfferStage, TenderDocumentStatus } from '@eapproval/offer-shared'
import { getStage } from '../../helpers'

const hasTenderDocumentContent = (tenderDocument = {}) => {
  return !(
    tenderDocument.annex1?.length === 0 &&
    tenderDocument.annex2?.length === 0 &&
    tenderDocument.annex3?.length === 0 &&
    tenderDocument.otherAnnexs?.every((annex) => annex.files.length === 0) &&
    (!tenderDocument.remark || tenderDocument.remark === '')
  )
}

const findLatestActiveTenderDocumentIndex = (tenderDocuments) => {
  for (let index = tenderDocuments.length - 1; index >= 0; index -= 1) {
    if (tenderDocuments[index]?.status !== TenderDocumentStatus.INACTIVE) {
      return index
    }
  }

  return -1
}

export const getTenderDocumentLabel = (tenderDocumentIndex) =>
  tenderDocumentIndex === 0 ? 'Tender' : `Addendum ${tenderDocumentIndex}`

export const prepareAndValidateSourcingPostData = ({
  offer,
  costBudgetDetail,
  costBudgetIndex = -1,
  selectedVendorIndex = -1,
  requireTenderDocumentContent = false,
  tenderDocumentIndex: selectedTenderDocumentIndex,
}) => {
  const costBudgetLabel = `Cost Budget ${costBudgetIndex + 1}: `

  if (!costBudgetDetail || !costBudgetDetail?._id || !Array.isArray(costBudgetDetail?.subcontractors)) {
    throw new Error(`${costBudgetLabel}Missing Cost Budget, please save first to proceed`)
  }

  if (!Array.isArray(costBudgetDetail.tenderDocuments) || costBudgetDetail.tenderDocuments.length === 0) {
    throw new Error(`${costBudgetLabel}Missing Tender Documents`)
  }

  if (isNullish(costBudgetDetail?.type) || String(costBudgetDetail.type).trim() === '') {
    throw new Error(`${costBudgetLabel}Missing Cost Budget Type`)
  }

  const tenderDocumentIndex = Number.isInteger(selectedTenderDocumentIndex)
    ? selectedTenderDocumentIndex
    : findLatestActiveTenderDocumentIndex(costBudgetDetail.tenderDocuments)

  const tenderDocument = costBudgetDetail.tenderDocuments[tenderDocumentIndex]

  if (!tenderDocument || tenderDocument.status === TenderDocumentStatus.INACTIVE) {
    throw new Error(`${costBudgetLabel}No Active Tender Document / Addendum Found`)
  }
  const vendorDetails =
    selectedVendorIndex === -1
      ? [...costBudgetDetail.subcontractors]
      : [costBudgetDetail.subcontractors[selectedVendorIndex]]

  if (vendorDetails.some((detail) => !detail?.dueDate)) {
    throw new Error(`${costBudgetLabel}Missing Vendor Due Date`)
  }

  if (requireTenderDocumentContent && !hasTenderDocumentContent(tenderDocument)) {
    throw new Error(
      `${costBudgetLabel}Please upload at least 1 annex file to ${getTenderDocumentLabel(tenderDocumentIndex)}.`
    )
  }

  const procurementOfficer = offer?.sourcing?.procurementOfficer

  if (!procurementOfficer) {
    throw new Error(`${costBudgetLabel}Missing Procurement Officer`)
  }

  const technicalUsers = []
  const ccList = []
  if (getStage(offer.status) === OfferStage.QUOTATION) {
    const sales = offer?.createdBy

    if (!sales) {
      throw new Error('Missing Sales')
    }
    technicalUsers.push(sales)
    ccList.push(sales)

    if (offer?.creator !== offer?.createdBy) {
      ccList.push(offer.creator)
    }
  } else {
    const bookingProjectManager = offer?.booking?.projectManager
    const engineers = offer?.engineers

    if (!bookingProjectManager) {
      throw new Error('Missing Booking Project Manager')
    }

    technicalUsers.push(bookingProjectManager, ...engineers)
    ccList.push(bookingProjectManager, ...engineers)
  }

  return {
    procurementOfficer,
    tenderDocumentIndex,
    vendorDetails,
    technicalUsers,
    ccList,
  }
}
